import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ShoppingBag, CreditCard, Trash2, Minus, Plus, X, ChevronLeft } from 'lucide-react';
import CashCalculator from '../components/CashCalculator';

const catStyles = {
  fundador: 'bg-purple-100 text-purple-800', normal: 'bg-stone-100 text-stone-800',
  frecuente: 'bg-orange-100 text-orange-800', eventos: 'bg-blue-100 text-blue-800',
};
const paymentMethods = [
  { value: 'efectivo', label: 'Efectivo' }, { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'cortesia', label: 'Cortesia' }, { value: 'cupon', label: 'Cupon' },
];

function ProductImage({ path, name, className }) {
  const [src, setSrc] = useState(null);
  const { API } = useAuth();
  useEffect(() => {
    if (!path) return;
    const token = localStorage.getItem('access_token');
    let blobUrl = null;
    axios.get(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' })
      .then(r => { blobUrl = URL.createObjectURL(r.data); setSrc(blobUrl); })
      .catch(() => {});
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path, API]);
  if (!path || !src) {
    return (
      <div className={`bg-stone-100 flex items-center justify-center ${className}`}>
        <ShoppingBag className="w-8 h-8 text-stone-300" strokeWidth={1} />
      </div>
    );
  }
  return <img src={src} alt={name} className={`object-cover ${className}`} />;
}

export { ProductImage };

export default function TouchPOSPage() {
  const { API, user: currentUser } = useAuth();
  const [visit, setVisit] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');

  // Visit selection
  const [visits, setVisits] = useState([]);
  const [braceletCode, setBraceletCode] = useState('');
  const braceletRef = useRef(null);

  const canCheckout = currentUser?.role === 'admin' || currentUser?.role === 'cajero';

  const fetchData = useCallback(async () => {
    try {
      const [p, c, s, v] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/product-categories`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/visits/active`)
      ]);
      setProducts(p.data.filter(pr => pr.active));
      setCategories(c.data);
      setSettings(s.data);
      setVisits(v.data);
    } catch {}
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const topCats = categories.filter(c => c.type === 'category');
  const getSubcats = (pid) => categories.filter(c => c.parent_id === pid);
  const getCatName = (id) => categories.find(c => c.id === id)?.name || '';

  const filteredProducts = products.filter(p => {
    if (selectedSub) return p.subcategory === selectedSub;
    if (selectedCat) return p.category === selectedCat;
    return true;
  });

  const searchBracelet = async () => {
    if (!braceletCode.trim()) return;
    try {
      const { data } = await axios.get(`${API}/visits/bracelet/${braceletCode.trim()}`);
      setVisit(data);
      setBraceletCode('');
    } catch {
      toast.error('Pulsera no encontrada');
    }
  };

  const selectVisit = (v) => setVisit(v);

  const refreshVisit = async () => {
    if (!visit) return;
    try {
      const { data } = await axios.get(`${API}/visits/${visit.id}`);
      setVisit(data);
    } catch {}
  };

  const addProduct = async (product) => {
    if (!visit) { toast.error('Selecciona una visita primero'); return; }
    try {
      const { data } = await axios.post(`${API}/visits/${visit.id}/orders`, { product_id: product.id, quantity: 1 });
      setVisit(data);
      toast.success(`${product.name} agregado`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const removeOrder = async (orderId) => {
    if (!visit) return;
    try {
      const { data } = await axios.delete(`${API}/visits/${visit.id}/orders/${orderId}`, { data: { reason: 'Eliminado desde POS tactil' } });
      setVisit(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleCheckout = async () => {
    if (!visit) return;
    try {
      await axios.post(`${API}/visits/${visit.id}/checkout`, { payment_method: paymentMethod, notes: '' });
      toast.success('Cobro realizado');
      setShowCheckout(false);
      setVisit(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const discPct = visit && settings ? (settings.category_discounts?.[visit.client_category] || 0) : 0;
  const subtotal = visit?.subtotal || 0;
  const expDisc = visit?.experience_discount || 0;
  const catDisc = Math.round(subtotal * discPct / 100 * 100) / 100;
  const finalTotal = Math.max(0, Math.round((subtotal - catDisc - expDisc) * 100) / 100);

  return (
    <div className="flex h-[calc(100vh-4rem)] md:h-[calc(100vh-2rem)] gap-0 -m-4 md:-m-8 lg:-m-10" data-testid="touch-pos-page">
      {/* Left: Products */}
      <div className="flex-1 flex flex-col bg-[#FAFAF9] overflow-hidden">
        {/* Category tabs */}
        <div className="flex items-center gap-2 p-3 bg-white border-b border-stone-200 overflow-x-auto flex-shrink-0">
          <button data-testid="pos-cat-all" onClick={() => { setSelectedCat(null); setSelectedSub(null); }}
            className={`px-5 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-all min-h-[48px] ${!selectedCat ? 'bg-orange-700 text-white shadow-sm' : 'bg-stone-100 text-stone-700 hover:bg-stone-200 active:bg-stone-300'}`}>
            Todos
          </button>
          {topCats.map(cat => (
            <button key={cat.id} data-testid={`pos-cat-${cat.id}`}
              onClick={() => { setSelectedCat(cat.id); setSelectedSub(null); }}
              className={`px-5 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-all min-h-[48px] ${selectedCat === cat.id ? 'bg-orange-700 text-white shadow-sm' : 'bg-stone-100 text-stone-700 hover:bg-stone-200 active:bg-stone-300'}`}>
              {cat.name}
            </button>
          ))}
        </div>

        {/* Subcategory tabs */}
        {selectedCat && getSubcats(selectedCat).length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-stone-50 border-b border-stone-200 overflow-x-auto flex-shrink-0">
            <button onClick={() => setSelectedSub(null)}
              className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap min-h-[40px] transition-all ${!selectedSub ? 'bg-orange-100 text-orange-800' : 'bg-white text-stone-600 hover:bg-stone-100'}`}>
              Todas
            </button>
            {getSubcats(selectedCat).map(sub => (
              <button key={sub.id} onClick={() => setSelectedSub(sub.id)}
                className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap min-h-[40px] transition-all ${selectedSub === sub.id ? 'bg-orange-100 text-orange-800' : 'bg-white text-stone-600 hover:bg-stone-100'}`}>
                {sub.name}
              </button>
            ))}
          </div>
        )}

        {/* Product grid */}
        <div className="flex-1 overflow-y-auto p-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filteredProducts.map(p => (
              <button key={p.id} data-testid={`pos-product-${p.id}`}
                onClick={() => addProduct(p)}
                disabled={!visit}
                className={`bg-white rounded-2xl border border-stone-200 overflow-hidden text-left transition-all hover:shadow-lg hover:border-orange-300 active:scale-95 ${!visit ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <ProductImage path={p.image_path} name={p.name} className="w-full h-24 sm:h-28 rounded-t-2xl" />
                <div className="p-3">
                  <p className="text-sm font-medium text-stone-900 line-clamp-1">{p.name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-lg font-outfit font-bold text-orange-700">${p.price.toFixed(2)}</span>
                    {p.stock !== null && p.stock !== undefined && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${p.stock <= 5 ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-600'}`}>{p.stock}</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
            {filteredProducts.length === 0 && (
              <div className="col-span-full text-center py-12 text-stone-400">Sin productos en esta categoria</div>
            )}
          </div>
        </div>
      </div>

      {/* Right: Order / Visit panel */}
      <div className="w-80 lg:w-96 bg-white border-l border-stone-200 flex flex-col flex-shrink-0">
        {!visit ? (
          <div className="flex-1 flex flex-col">
            <div className="p-4 border-b border-stone-200">
              <h2 className="font-outfit font-semibold text-stone-900 text-lg mb-3">Seleccionar Cuenta</h2>
              <div className="flex gap-2 mb-3">
                <Input ref={braceletRef} data-testid="pos-bracelet-input" value={braceletCode} onChange={e => setBraceletCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchBracelet()}
                  placeholder="Pulsera RFID..." className="rounded-xl flex-1 h-12 text-base" />
                <Button onClick={searchBracelet} className="bg-orange-700 hover:bg-orange-800 text-white rounded-xl h-12 px-4 min-h-[48px]">Buscar</Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <p className="text-xs text-stone-500 uppercase tracking-wider mb-2 font-semibold">Visitas activas</p>
              <div className="space-y-2">
                {visits.map(v => (
                  <button key={v.id} data-testid={`pos-visit-${v.id}`} onClick={() => selectVisit(v)}
                    className="w-full text-left bg-stone-50 hover:bg-orange-50 hover:border-orange-300 border border-stone-200 rounded-xl p-3 transition-all min-h-[56px] active:scale-[0.98]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-stone-900">{v.client_name}</p>
                        <p className="text-xs text-stone-500">{v.locker_number ? `Locker #${v.locker_number}` : ''} {v.bracelet_code ? `| ${v.bracelet_code}` : ''}</p>
                      </div>
                      <span className="text-sm font-bold text-orange-700">${(v.total || 0).toFixed(2)}</span>
                    </div>
                  </button>
                ))}
                {visits.length === 0 && <p className="text-sm text-stone-400 text-center py-6">Sin visitas activas</p>}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col">
            {/* Visit header */}
            <div className="p-4 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <button data-testid="pos-back-btn" onClick={() => setVisit(null)} className="p-2 rounded-xl hover:bg-stone-100 active:bg-stone-200 min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <ChevronLeft className="w-5 h-5 text-stone-600" />
                </button>
                <div className="text-right">
                  <p className="font-medium text-stone-900">{visit.client_name}</p>
                  <div className="flex items-center gap-2 justify-end">
                    <Badge className={`${catStyles[visit.client_category] || catStyles.normal} border-0 rounded-full text-xs`}>{visit.client_category}</Badge>
                    {visit.locker_number && <span className="text-xs text-stone-500">Locker #{visit.locker_number}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Orders list */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {(visit.orders || []).map(o => (
                <div key={o.id} className="flex items-center gap-3 bg-stone-50 rounded-xl p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{o.product_name}</p>
                    <p className="text-xs text-stone-500">{o.quantity} x ${o.unit_price.toFixed(2)}</p>
                  </div>
                  <span className="text-sm font-bold text-stone-900">${o.total.toFixed(2)}</span>
                  {canCheckout && !o.is_entry_fee && o.product_id !== 'entry_fee' && (
                    <button data-testid={`pos-remove-${o.id}`} onClick={() => removeOrder(o.id)}
                      className="p-2 rounded-lg hover:bg-red-100 text-stone-400 hover:text-red-600 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {(visit.orders || []).length === 0 && (
                <p className="text-sm text-stone-400 text-center py-8">Toca un producto para agregar</p>
              )}
            </div>

            {/* Total + Checkout */}
            <div className="p-4 border-t border-stone-200 bg-stone-50">
              {discPct > 0 && <div className="flex justify-between text-xs text-orange-700 mb-1"><span>Descuento {discPct}%</span><span>-${catDisc.toFixed(2)}</span></div>}
              {expDisc > 0 && <div className="flex justify-between text-xs text-purple-700 mb-1"><span>Cortesia</span><span>-${expDisc.toFixed(2)}</span></div>}
              <div className="flex items-center justify-between mb-3">
                <span className="font-outfit text-lg font-semibold text-stone-900">Total</span>
                <span className="font-outfit text-3xl font-bold text-orange-700">${finalTotal.toFixed(2)}</span>
              </div>
              {canCheckout && (
                <Button data-testid="pos-checkout-btn" onClick={() => setShowCheckout(true)}
                  className="w-full bg-green-700 hover:bg-green-800 text-white rounded-xl h-14 text-lg font-semibold transition-all active:scale-[0.98] min-h-[56px]">
                  <CreditCard className="w-5 h-5 mr-2" /> Cobrar
                </Button>
              )}
              {!canCheckout && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 text-center">Solo cajero o admin puede cobrar</p>}
            </div>
          </div>
        )}
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Cobrar</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-center py-2">
              <p className="font-outfit text-4xl font-bold text-orange-700">${finalTotal.toFixed(2)}</p>
            </div>
            <div>
              <Label>Metodo de Pago</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="pos-payment" className="rounded-xl mt-1 h-12"><SelectValue /></SelectTrigger>
                <SelectContent>{paymentMethods.map(pm => <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {paymentMethod === 'efectivo' && <CashCalculator total={finalTotal} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)} className="rounded-full h-12 min-h-[48px]">Cancelar</Button>
            <Button data-testid="pos-checkout-confirm" onClick={handleCheckout} className="bg-green-700 hover:bg-green-800 text-white rounded-full h-12 min-h-[48px]">Confirmar Cobro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
